/**
 * Horizontally scrolling path map: level headers, lesson nodes (first = big polaroid "container"
 * card, others = round thumbnails with a progress ring), keys between levels, a dashed trail and a
 * final "coming soon" node. Auto-scrolls to the current lesson.
 */
import { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { chapterComic, itemCount, stepCount } from '@sketchling/core';
import { useStoryFlags } from './story/StoryKit';
import { exhibitionStatus, isNewForProfile, playgroundOnlyPictures, nodeStatus, pathFraction, type Access, type ActivityType, type NodeStatus, type PathConfig, type PathUnitItem, type ProgressState, type StoryChapter } from '@sketchling/core';
import { FinaleMoment, consumeFinaleCue } from './FinaleMoment';
import { toast } from './ui';
import '../styles/curriculum-v2.css';
import { CONTENT, IX, drawingById, useStoriesReady } from '../lib/content';
import { useLoc, useT, type TKey } from '../lib/i18n';
import { DrawingView } from './DrawingView';
import { Vignette, hasVignette } from '../art/vignette/Vignette';
import { Icon, type IconName } from './icons';
import { WorldMapScene, WorldTrail, isPortrait, useWorldRaster } from './WorldMap';
import { WORLD_ART } from '../art/worlds';
import { useArtStyle } from '../art/useArtStyle';
import { CurvedArrow, NextArrows, SmallCheck } from '../anim';
import { useApp } from '../state/store';
import { play } from '../lib/sfx';
import { BloomProps, ColourBackdrop, ColourChip, ColourVeil, MapCreatures, useFreshBlooms, type CwNode } from './PathMapColour';
import { useSketch } from './PathMapSketch';

/** Chapter comics load on demand (not on the start screen). */
const ComicModal = lazy(() => import('./story/ComicPage').then((m) => ({ default: m.ComicModal })));

interface Node {
  item: PathUnitItem;
  unitIndex: number;
  level: number;
  status?: NodeStatus;
  x: number;
  y: number;
  w: number;
  /** v2: the level's story chapter (header sign). */
  chapter?: StoryChapter;
  /** v2 side branch (challenge / observe) next to the level key: centre of the key it hangs from. */
  anchor?: { x: number; y: number };
  /** v2: shown "New!" (grandfathered for this profile). */
  fresh?: boolean;
}

const BASE_W: Record<string, number> = { header: 124, container: 172, circular: 92, key: 60, comingSoon: 150, text: 124, finale: 120, gallery: 112 };
const GAP = 46;

/** v2 map icon per activity type. */
export const ACTIVITY_ICON: Record<ActivityType, IconName> = {
  doodle: 'bolt',
  core: 'pencil',
  remix: 'flip',
  colouring: 'theme',
  scene: 'layout',
  masterpiece: 'trophy',
  thenNow: 'timer',
  challenge: 'star',
  observe: 'eye',
  crossover: 'sparkle',
  picture: 'gallery',
};

export function PathMap({
  path,
  progress,
  access,
  showHand,
  onNodeTap,
}: {
  path: PathConfig;
  progress: ProgressState;
  access: Access;
  showHand: boolean;
  onNodeTap: (item: PathUnitItem, status: NodeStatus) => void;
}) {
  const t = useT();
  const loc = useLoc();
  const scroller = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 600, h: 300 });
  const h = box.h;

  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const measure = () => setBox((b) => (b.w === el.clientWidth && b.h === el.clientHeight ? b : { w: el.clientWidth, h: el.clientHeight }));
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, []);

  // Idle loops (pulse, bobbing badges, next arrows) only for nodes on screen: mark them data-iv (styles/power.css).
  useEffect(() => {
    const root = scroller.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (es) => {
        for (const e of es) {
          const el = e.target as HTMLElement;
          if (e.isIntersecting) el.dataset.iv = '1';
          else delete el.dataset.iv;
        }
      },
      { root, rootMargin: '0px 80px' },
    );
    const watch = () => root.querySelectorAll('.pnode').forEach((n) => io.observe(n));
    watch();
    const mo = new MutationObserver(watch);
    mo.observe(root.querySelector('.path-track') ?? root, { childList: true });
    return () => (io.disconnect(), mo.disconnect());
  }, [path.id]);

  // Node scale from the map's height: compact phones (≈ 330 px map) ≈ 0.86, iPads / desktop 1.4 (docs/RESPONSIVE.md).
  const s = Math.max(0.72, Math.min(1.4, h / 385, (box.w + 240) / 600));
  const amp = Math.min(80 * s, Math.max(0, h / 2 - 96 * s) * 0.55);

  const styleId = useArtStyle();
  const theme = WORLD_ART[path.id].theme;
  const portrait = isPortrait();
  // The map always scrolls sideways, so portrait screens use the landscape painting too (its trail runs sideways like
  // the nodes; the tall portrait paintings wind upwards and the nodes crossed them, HANDOFF nit 10). In portrait the
  // painting shows its full height, so the trail band has its own numbers (`raster.portrait`).
  const painted = useWorldRaster(path.id, false);
  const band = portrait && theme.raster.portrait ? theme.raster.portrait : theme.raster;
  // WOW · colour: the map starts as a pencil sketch and blooms into colour around done lessons.
  const colourWorld = useApp((st) => st.config.enableColourWorld);
  const profileId = useApp((st) => st.activeProfileId);
  // Curriculum v2 switches (side branches, story chapter signs).
  const showChallenges = useApp((st) => st.config.enableLevelChallenges);
  const showObserve = useApp((st) => st.config.enableObserve);
  const stories = useApp((st) => st.config.enableWorldStories);
  // Picture tier: a small "picture" node hangs from each object node; the exhibition room follows the finale.
  const pictures = useApp((st) => st.config.pictureTier);
  const navigate = useNavigate();
  const expo = useMemo(() => exhibitionStatus(progress, path, access, playgroundOnlyPictures(CONTENT.drawings, path.id)), [progress, path, access]);
  const worldDone = progress.completedPaths.includes(path.id) || pathFraction(progress, path) >= 1;
  const [finale, setFinale] = useState(false);
  // The finale moment plays once right after the world's last activity (cue set by the lesson), and on tap.
  useEffect(() => {
    if (consumeFinaleCue(path.id)) setFinale(true);
  }, [path.id]);

  const { nodes, width, currentIdx } = useMemo(() => {
    const out: Node[] = [];
    let x = 36 * s;
    let picSide = 0;
    path.units.forEach((u, ui) => {
      const pending: PathUnitItem[] = [];
      for (const item of u.items) {
        if (item.branch === 'picture' || (item.type === 'gallery' && !pictures)) continue;
        if (item.branch) {
          if ((item.branch === 'challenge' && showChallenges) || (item.branch === 'observe' && showObserve)) pending.push(item);
          continue;
        }
        const chapter = item.type === 'header' && stories && u.chapter ? u.chapter : undefined;
        const w = (chapter ? 150 : (BASE_W[item.type] ?? 100)) * s;
        const flat = item.type === 'header' || item.type === 'container';
        const sine = Math.sin((x + w / 2) / (150 * s));
        // Painted map: keep nodes in the painting's trail band; SVG map: centred wave.
        const y0 = painted ? h * band.mid + (flat ? 0 : sine * Math.min(h * band.amp, amp)) : h / 2 + (flat ? 0 : sine * amp);
        // The lesson card (≈ 190 px tall at scale 1) never hangs below the map's edge (short / portrait maps).
        const y = item.type === 'container' ? Math.min(y0, h - 110 * s - 8) : y0;
        const status = item.miniUnitId ? nodeStatus(progress, path, item, access) : undefined;
        const fresh = !!item.miniUnitId && isNewForProfile(progress, path, item);
        const node: Node = { item, unitIndex: ui, level: u.level, status, x: x + w / 2, y, w, ...(chapter ? { chapter } : {}), ...(fresh ? { fresh } : {}) };
        out.push(node);
        x += w + GAP * s;
        // Picture tier: the object's picture node(s) hang above / below it (remix: one per card, side by side).
        const pics = pictures && item.miniUnitId ? u.items.filter((b) => b.branch === 'picture' && b.pictureOf === item.id) : [];
        if (pics.length) {
          const pw = 50 * s;
          // Alternate above / below along the trail (below clears the node's label); flip when there is no room.
          const reachUp = (item.type === 'container' ? 112 : 84) * s;
          const reachDown = reachUp + 22 * s;
          let up = picSide++ % 2 === 0;
          if (up && node.y - reachUp < pw / 2 + 4) up = false;
          else if (!up && node.y + reachDown > h - pw / 2 - 14 * s) up = true;
          pics.forEach((b, j) => {
            // Remix: the card pictures fan out in a small arc.
            const off = j - (pics.length - 1) / 2;
            const bx = node.x + off * pw * 0.98;
            const by = Math.max(pw / 2 + 2, Math.min(h - pw / 2 - 14 * s, node.y + (up ? -1 : 1) * ((up ? reachUp : reachDown) - Math.abs(off) * pw * 0.32)));
            out.push({ item: b, unitIndex: ui, level: u.level, status: nodeStatus(progress, path, b, access), x: bx, y: by, w: pw, anchor: { x: node.x, y: node.y } });
          });
        }
        // Side branches hang above / below the level key (or the finale), off the main trail.
        if ((item.type === 'key' || item.type === 'finale') && pending.length) {
          const bw = 80 * s;
          const lift = 74 * s + bw / 2;
          pending.forEach((b, j) => {
            const up = (j % 2 === 0) === node.y > h / 2;
            const by = Math.max(bw / 2 + 4, Math.min(h - bw / 2 - 18 * s, node.y + (up ? -lift : lift) * (1 + Math.floor(j / 2) * 0.9)));
            const bx = node.x + (j >= 2 ? (j % 2 ? 1 : -1) * bw * 0.7 : 0);
            const status = nodeStatus(progress, path, b, access);
            out.push({ item: b, unitIndex: ui, level: u.level, status, x: bx, y: by, w: bw, anchor: { x: node.x, y: node.y } });
          });
          pending.length = 0;
        }
      }
    });
    const main = (n: Node) => !n.anchor;
    let cur = out.findIndex((n) => main(n) && n.status === 'current');
    if (cur < 0) cur = out.findIndex((n) => n.status === 'current');
    return { nodes: out, width: x + 24 * s, currentIdx: cur >= 0 ? cur : out.findIndex((n) => n.status === 'premium') };
  }, [path, progress, access, s, amp, h, painted, band, showChallenges, showObserve, stories, pictures]);

  // Auto-scroll to the current node.
  useLayoutEffect(() => {
    const el = scroller.current;
    const n = nodes[currentIdx];
    if (!el || !n) return;
    el.scrollTo({ left: Math.max(0, n.x - el.clientWidth * 0.38), behavior: 'auto' });
    // when the path or the layout scale changes (not on every progress update)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path.id, s, currentIdx >= 0]);

  // The trail joins the main-line nodes only (side branches hang off it).
  const trail = useMemo(() => nodes.filter((n) => !n.anchor), [nodes]);
  const doneUpTo = currentIdx >= 0 ? Math.max(0, trail.indexOf(nodes[currentIdx]!) >= 0 ? trail.indexOf(nodes[currentIdx]!) : trail.filter((n) => nodes.indexOf(n) < currentIdx).length - 1) : trail.length - 1;

  const cwNodes = useMemo<CwNode[]>(
    () =>
      trail.map((n) => ({
        key: n.item.miniUnitId ?? n.item.id,
        x: n.x,
        y: n.y,
        w: n.w,
        done: n.status === 'done',
        level: n.level,
        circle: n.item.type === 'circular',
        drawingId: n.item.miniUnitId ? IX.miniUnits.get(n.item.miniUnitId)?.drawingId : undefined,
      })),
    [trail],
  );
  const lessonCount = cwNodes.filter((n) => n.drawingId).length;
  const doneKeys = useMemo(() => cwNodes.filter((n) => n.done).map((n) => n.key), [cwNodes]);
  const colourPct = lessonCount ? doneKeys.length / lessonCount : 0;
  const fresh = useFreshBlooms(profileId, path.id, colourWorld ? doneKeys : []);
  const sketch = useSketch(colourWorld && painted ? painted : null);
  // Level zones of the scenic map (contiguous x-ranges: meadow → pond → forest → hill, seasons…).
  const zones = useMemo<[number, number][]>(() => {
    const ends = path.units.map((_, ui) => {
      const xs = nodes.filter((n) => n.unitIndex === ui).map((n) => n.x);
      return [Math.min(...xs), Math.max(...xs)] as const;
    });
    return ends.map((e, i) => [i === 0 ? 0 : (ends[i - 1]![1] + e[0]) / 2, i === ends.length - 1 ? width : (e[1] + ends[i + 1]![0]) / 2] as [number, number]);
  }, [nodes, path.units, width]);
  const vars = {
    width,
    height: '100%',
    ['--s' as string]: s,
    ['--w-ink' as string]: theme.ink,
    ['--w-label' as string]: theme.label,
    ['--w-ring' as string]: theme.node.ring,
    ['--w-done' as string]: theme.node.done,
    ['--w-disc' as string]: theme.node.disc,
    ['--w-locked' as string]: theme.node.locked,
    ['--w-card' as string]: theme.card.bg,
  };

  const map = (
    <div
      className={`path-map world-map w-${path.id}${theme.dark ? ' w-dark' : ''}${painted ? ' painted' : ''}${colourWorld ? ' cw' : ''}`}
      ref={scroller}
      data-world-raster={painted ? 'yes' : 'no'}
      style={painted && !colourWorld ? { backgroundImage: `linear-gradient(${theme.raster.scrim}, ${theme.raster.scrim}), url(${painted})` } : undefined}
      onScroll={(e) => {
        if (!painted) return;
        // Gentle parallax: the painting pans from its left to its right edge across the whole path.
        const el = e.currentTarget;
        const f = el.scrollWidth > el.clientWidth ? el.scrollLeft / (el.scrollWidth - el.clientWidth) : 0;
        el.style.backgroundPosition = `${(f * 100).toFixed(1)}% center`;
      }}
      data-testid="path-map"
      onWheel={(e) => {
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) e.currentTarget.scrollLeft += e.deltaY;
      }}
    >
      <div className="path-track" style={vars}>
        {!painted && <WorldMapScene world={path.id} styleId={styleId} width={width} height={h} zones={zones} />}
        {colourWorld && !painted && <ColourVeil nodes={cwNodes} width={width} h={h} s={s} progress={colourPct} />}
        <WorldTrail world={path.id} points={trail} doneUpTo={doneUpTo} width={width} height={h} scale={s} painted={!!painted} />
        {colourWorld && <BloomProps world={path.id} nodes={cwNodes} fresh={fresh} width={width} h={h} s={s} />}
        {colourWorld && <MapCreatures world={path.id} nodes={cwNodes} h={h} s={s} />}
        {nodes.map((n, k) => {
          const prev = nodes[k - 1];
          if (n.anchor) {
            const dx = n.x - n.anchor.x;
            const dy = n.y - n.anchor.y;
            return (
              <div key={n.item.id}>
                <span className="pspur" style={{ left: n.anchor.x, top: n.anchor.y, width: Math.hypot(dx, dy), transform: `rotate(${Math.atan2(dy, dx)}rad)` }} aria-hidden />
                <PathNode node={n} loc={loc} t={t} progress={progress} pathId={path.id} onTap={onNodeTap} hand={false} />
              </div>
            );
          }
          if (n.item.type === 'gallery')
            return (
              <button
                key={n.item.id}
                type="button"
                className={`pnode pnode-finale pnode-gallery${expo.open ? ' ready' : ' locked'}`}
                style={{ left: n.x - n.w / 2, top: n.y, width: n.w }}
                onClick={() => {
                  // The room opens with the first painted picture; the node keeps its locked look until the set is done.
                  if (expo.open || expo.done > 0) navigate(`/exhibition/${path.id}`);
                  else toast(t('v2.galleryLocked', { done: expo.done, total: expo.total }));
                }}
                aria-label={expo.open ? t('v2.galleryOpen') : t('v2.galleryLocked', { done: expo.done, total: expo.total })}
                data-testid="path-gallery"
                data-ready={expo.open ? 'yes' : 'no'}
                data-tappable={expo.open || expo.done > 0 ? 'yes' : 'no'}
              >
                <span className="v2pf-book">
                  <Icon name={expo.open ? 'gallery' : 'lock'} size={28} />
                </span>
                <b>{t('v2.gallery')}</b>
                <small>{expo.open ? t('v2.galleryOpen') : `${expo.done}/${expo.total}`}</small>
              </button>
            );
          if (n.item.type === 'finale')
            return (
              <button
                key={n.item.id}
                type="button"
                className={`pnode pnode-finale${worldDone ? ' ready' : ' locked'}`}
                style={{ left: n.x - n.w / 2, top: n.y, width: n.w }}
                onClick={() => {
                  if (worldDone) setFinale(true);
                  else toast(t('v2.finaleLocked'));
                }}
                aria-label={worldDone ? t('v2.finalePlay') : t('v2.finaleLocked')}
                data-testid="path-finale"
                data-ready={worldDone ? 'yes' : 'no'}
              >
                <span className="v2pf-book">
                  <Icon name={worldDone ? 'book' : 'lock'} size={28} />
                </span>
                <b>{n.item.title ? loc(n.item.title) : ''}</b>
                <small>{worldDone ? t('v2.finalePlay') : t('v2.finaleLocked')}</small>
              </button>
            );
          return (
            <div key={n.item.id}>
              {n.status === 'keyLocked' && prev && !prev.anchor && (
                <span className="connector-lock" style={{ left: (prev.x + n.x) / 2, top: (prev.y + n.y) / 2 }} aria-label={t('learn.keyNeeded')}>
                  <Icon name="key" size={16} />
                </span>
              )}
              <PathNode node={n} loc={loc} t={t} progress={progress} pathId={path.id} onTap={onNodeTap} hand={showHand && k === currentIdx && n.status === 'current'} />
            </div>
          );
        })}
      </div>
    </div>
  );
  const finaleEl = finale && path.story ? <FinaleMoment path={path} onClose={() => setFinale(false)} /> : null;
  if (!colourWorld)
    return finaleEl ? (
      <>
        {map}
        {finaleEl}
      </>
    ) : (
      map
    );
  return (
    <div className={`cw-frame w-${path.id}`} data-testid="colour-world" data-colour-pct={Math.round(colourPct * 100)}>
      {painted && <ColourBackdrop scroller={scroller} colour={painted} sketch={sketch} scrim={theme.raster.scrim} nodes={cwNodes} fresh={fresh} progress={colourPct} h={h} s={s} />}
      {map}
      <ColourChip progress={colourPct} blooming={fresh.size > 0} />
      {finaleEl}
    </div>
  );
}

function PathNode({
  node,
  loc,
  t,
  progress,
  pathId,
  onTap,
  hand,
}: {
  node: Node;
  loc: ReturnType<typeof useLoc>;
  t: ReturnType<typeof useT>;
  progress: ProgressState;
  pathId: PathConfig['id'];
  onTap: (item: PathUnitItem, status: NodeStatus) => void;
  hand: boolean;
}) {
  const { item, x, y, w, status } = node;
  const style = { left: x - w / 2, top: y, width: w };
  if (item.type === 'header' || item.type === 'text')
    return (
      <div className={`pnode pnode-header${node.chapter ? ' has-chapter' : ''}`} style={style} data-testid={node.chapter ? `path-chapter-${node.level}` : undefined}>
        <span className="caps">{t('levelCaps', { n: node.level })}</span>
        <b>{item.title ? loc(item.title) : ''}</b>
        {node.chapter && (
          <span className="pchapter">
            {t('v2.chapter', { n: node.level })} · {loc(node.chapter.title)}
            <i>{loc(node.chapter.beat)}</i>
            <ChapterComicButton world={pathId} level={node.level} done={(progress.pathKeys[pathId] ?? 0) >= node.level} label={t('sty.comic.replay', { n: node.level })} />
          </span>
        )}
      </div>
    );
  if (item.type === 'key') {
    const earned = (progress.pathKeys[pathId] ?? 0) >= node.level;
    return (
      <div className={`pnode pnode-key${earned ? ' earned' : ''}`} style={style} data-testid={`path-key-${node.level}`} aria-label={earned ? t('learn.keyEarned') : t('learn.keyLocked')}>
        <Icon name="key" />
      </div>
    );
  }
  if (item.type === 'comingSoon')
    return (
      <div className="pnode pnode-soon" style={style} data-testid="path-coming-soon">
        <Icon name="sparkle" />
        <b>{t('comingSoon')}</b>
        <small>{item.title ? loc(item.title) : ''}</small>
      </div>
    );

  const mu = item.miniUnitId ? IX.miniUnits.get(item.miniUnitId) : undefined;
  const drawing = mu ? drawingById(mu.drawingId) : undefined;
  const title = item.title ? loc(item.title) : mu ? loc(mu.title) : '';
  const st = status ?? 'locked';
  const stepIdx = item.miniUnitId ? progress.miniUnitSteps[item.miniUnitId] : undefined;
  const frac = st === 'done' ? 1 : mu && stepIdx !== undefined ? Math.min(1, (stepIdx + 1) / Math.max(1, itemCount(mu))) : 0;
  const badge =
    st === 'done' ? (
      <span className="pbadge done">
        <SmallCheck size={16} />
      </span>
    ) : st === 'premium' ? (
      // Monetization §5.4: premium nodes are a pencil sketch with a small wish star (tap → wish screen).
      <span className="pbadge wish" data-testid="pbadge-wish">
        <Icon name="star" size={15} />
      </span>
    ) : st === 'locked' || st === 'keyLocked' ? (
      <span className="pbadge lock">
        <Icon name="lock" size={16} />
      </span>
    ) : null;
  const label = `${title}${item.activity ? ` · ${t(`v2.act.${item.activity}` as TKey)}` : ''}${node.fresh ? ` (${t('v2.new')})` : ''}${st === 'done' ? ` (${t('learn.done')})` : st === 'premium' ? ` (${t('premium')})` : st === 'current' ? '' : ` (${t('locked')})`}`;
  // v2: activity icon + "New!" (activity added to a level this profile had already reached).
  const extra = (
    <>
      {item.activity && (
        <span className={`pact a-${item.activity}`} aria-hidden data-activity={item.activity}>
          <Icon name={ACTIVITY_ICON[item.activity]} size={14} />
        </span>
      )}
      {node.fresh && (
        <span className="pbadge new" data-testid={`path-new-${item.miniUnitId}`}>
          {t('v2.new')}
        </span>
      )}
    </>
  );

  if (item.type === 'container')
    return (
      <button type="button" className={`pnode pnode-container st-${st}`} style={style} onClick={() => onTap(item, st)} aria-label={label} data-testid={`path-node-${item.miniUnitId}`} data-status={st} data-activity={item.activity} data-new={node.fresh ? 'yes' : undefined}>
        <div className="polaroid">
          <div className="photo">{drawing && (hasVignette(drawing, item.miniUnitId) ? <Vignette drawing={drawing} activityId={item.miniUnitId} /> : <DrawingView drawing={drawing} paper={!drawing.art} />)}</div>
          <div className="pc-title">{title}</div>
          <div className="pc-meta">
            {t('learn.stepsMin', { n: stepCount(drawing), m: drawing?.minutes ?? 5 })}
            {st === 'current' && <span className="chip on">{frac > 0 ? t('learn.resume') : t('start')}</span>}
          </div>
        </div>
        {badge}
        {extra}
        {hand && <TippingHand />}
      </button>
    );

  const R = 46;
  const C = 2 * Math.PI * R;
  return (
    <button
      type="button"
      className={`pnode pnode-circle st-${st}${node.anchor ? ` pnode-branch b-${item.branch}` : ''}`}
      style={style}
      onClick={() => onTap(item, st)}
      aria-label={label}
      data-testid={`path-node-${item.miniUnitId}`}
      data-status={st}
      data-activity={item.activity}
      data-branch={item.branch}
      data-new={node.fresh ? 'yes' : undefined}
    >
      <span className="pc-disc">
        <span className="pc-thumb">{drawing && (hasVignette(drawing, item.miniUnitId) ? <Vignette drawing={drawing} activityId={item.miniUnitId} /> : <DrawingView drawing={drawing} paper={!drawing.art} />)}</span>
        <svg className="pc-ring" viewBox="0 0 100 100" aria-hidden>
          <circle cx="50" cy="50" r={R} className="ring-bg" />
          {frac > 0 && <circle cx="50" cy="50" r={R} className="ring-fg" strokeDasharray={`${C * frac} ${C}`} />}
        </svg>
        {badge}
        {extra}
      </span>
      <span className="pc-label">{title}</span>
      {st === 'current' && <NextArrows className="pn-next" />}
      {hand && <TippingHand />}
      {hand && <CurvedArrow className="pn-curve" />}
    </button>
  );
}

/** Animated pointing hand that invites the first tap. */
export function TippingHand() {
  return (
    <svg className="tipping-hand" viewBox="0 0 64 64" aria-hidden>
      <path
        d="M24 30V12a5 5 0 0110 0v14l3-1a5 5 0 016 3l1 2 3-1a5 5 0 016 4l1 9c1 8-5 16-14 16h-6c-5 0-9-3-11-7l-8-13a5 5 0 018-6z"
        fill="#FFFBF4"
        stroke="#132630"
        strokeWidth="3"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Map chapter sign → replay the chapter comic (story layer, flag storyComics). */
function ChapterComicButton({ world, level, done, label }: { world: string; level: number; done: boolean; label: string }) {
  const flags = useStoryFlags();
  const [open, setOpen] = useState(false);
  // The world's story layer (≈ 60 KB) loads with the map; the sign appears once it is in.
  const storyReady = useStoriesReady([world]);
  if (!storyReady || !flags.comics || !chapterComic(world, level, 'start')) return null;
  return (
    <>
      <button
        type="button"
        className="sty-sign-btn"
        aria-label={label}
        title={label}
        data-testid={`chapter-comic-${level}`}
        onClick={(e) => {
          e.stopPropagation();
          play('pageTurn');
          setOpen(true);
        }}
      >
        <Icon name="book" size={16} />
      </button>
      {open &&
        createPortal(
          <Suspense fallback={null}>
            <ComicModal world={world} level={level} levelDone={done} onClose={() => setOpen(false)} />
          </Suspense>,
          document.body,
        )}
    </>
  );
}
